import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  makeStyles,
  Menu,
  styled,
  PopoverOrigin,
  withStyles
} from '@material-ui/core';

import SMenuItem, { ISMenuItem } from './MenuItem';
import SMoreButton from '../smore-button';
import eventEmitter from '../../libs/EventEmitter';

const SUBMENU_HOVER_DELAY = 400; // ms delay before showing submenu on hover

const StyledMenu = withStyles(({ spacing }) => ({
  paper: {
    marginTop: spacing(2),
    padding: `0px ${spacing(0)}px`,
    borderRadius: 8,
    left: spacing(0),

    '& ul': {
      paddingTop: 0,
      paddingBottom: 0,

      '& div > li > div': {
        maxHeight: 60,
        height: '100%'
      }
    }
  }
}))(Menu);

export const StyledMenuV2 = styled(Menu)(({ theme }) => ({
  margin: theme.spacing('2px', 1),

  '& .MuiPaper-root': {
    borderRadius: 20,
    minWidth: 178,
    boxShadow:
      '0px 24px 48px -8px rgba(48, 54, 60, 0.12), 0px 4px 4px 0px rgba(48, 54, 60, 0.04)',

    '& ul': {
      padding: 12,

      '& div > li > div': {
        borderRadius: 24,
        maxHeight: 48,
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',

        padding: theme.spacing('12px', '12px')
      },

      // Submenu trigger items wrap the MenuItem in an extra container, so the
      // hover background sits on a deeper div. Round it too so the highlight
      // matches the rest of the menu.
      '& div > li > div > div > div': {
        borderRadius: 24
      }
    }
  }
}));

const useIconButtonStyles = makeStyles(() => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center'
  }
}));

const defaultSubmenuAnchorOrigin: PopoverOrigin = {
  horizontal: 'left',
  vertical: 'top'
};

export interface ISDropdownMenu {
  menuItems: ISMenuItem[];
  children?: JSX.Element;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  submenuTransformOrigin?: PopoverOrigin;
  submenuAnchorOrigin?: PopoverOrigin;
  onClose?: () => void;
  customMenuClass?: string;
  hasSubmenu?: boolean;
  direction?: 'horizontal' | 'vertical';
  // ! This will be removed once the new design is implemented
  newDesign?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  disabledTooltipPlacement?: 'left' | 'right' | 'top' | 'bottom';
}

const SDropdownMenu = ({
  newDesign = false,
  menuItems,
  onClose,
  children,
  hasSubmenu,
  anchorOrigin,
  transformOrigin,
  customMenuClass,
  submenuAnchorOrigin = defaultSubmenuAnchorOrigin,
  submenuTransformOrigin,
  direction = 'vertical',
  disabled,
  disabledTooltip,
  disabledTooltipPlacement
}: ISDropdownMenu) => {
  const styles = useIconButtonStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      eventEmitter.emit('sdropdown-menu-opened');
    },
    []
  );

  const handleOpenMenuWithDelay = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      // Capture the target element before the timeout (event.currentTarget is only valid synchronously)
      const targetElement = event.currentTarget;

      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Set a new timeout to open the menu after the delay
      hoverTimeoutRef.current = setTimeout(() => {
        setAnchorEl(targetElement);
        eventEmitter.emit('sdropdown-menu-opened');
      }, SUBMENU_HOVER_DELAY);
    },
    []
  );

  const handleCancelHoverTimeout = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  // Cleanup timeout on unmount to prevent state updates on unmounted component
  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    []
  );

  const handleClose = useCallback(() => {
    handleCancelHoverTimeout();
    setAnchorEl(null);
    onClose?.();
  }, [onClose, handleCancelHoverTimeout]);

  const SMenu = newDesign ? StyledMenuV2 : StyledMenu;
  // Match regular MenuItem geometry: regular items have inline `padding: 0`
  // which overrides the cascaded `padding: 12px 12px` on `& div > li > div`,
  // so we must do the same for the submenu trigger's buttonContainer or it
  // ends up with extra horizontal padding that shrinks content and clips the
  // chevron. Height is left to the cascade (maxHeight: 48) so submenu
  // triggers visually match non-submenu rows like "Delete".
  const subMenuStyle = { width: '100%', padding: 0 };

  return (
    <Box
      className={styles.buttonContainer}
      onClick={e => e.stopPropagation()}
      style={hasSubmenu && newDesign ? subMenuStyle : {}}>
      <Box
        width="100%"
        onClick={disabled ? undefined : handleOpenMenu}
        onMouseEnter={hasSubmenu ? handleOpenMenuWithDelay : undefined}
        onMouseLeave={hasSubmenu ? handleCancelHoverTimeout : undefined}>
        {children || (
          <SMoreButton
            active={Boolean(anchorEl)}
            width={14}
            direction={direction}
            disabled={disabled}
            tooltip={disabledTooltip}
            tooltipPlacement={disabledTooltipPlacement}
          />
        )}
      </Box>
      {Boolean(anchorEl) && (
        <SMenu
          anchorEl={anchorEl}
          keepMounted
          open
          onClose={hasSubmenu ? () => setAnchorEl(null) : handleClose}
          elevation={3}
          getContentAnchorEl={null}
          anchorOrigin={
            anchorOrigin || {
              vertical: 'bottom',
              horizontal: 'right'
            }
          }
          transformOrigin={
            transformOrigin || {
              vertical: 'top',
              horizontal: 'right'
            }
          }
          className={customMenuClass}>
          <div
            onMouseLeave={
              hasSubmenu
                ? () => {
                    handleCancelHoverTimeout();
                    setAnchorEl(null);
                  }
                : undefined
            }>
            {menuItems.map(item => (
              <SMenuItem
                key={item.text}
                {...item}
                handleClose={handleClose}
                newDesign={newDesign}
                submenuAnchorOrigin={submenuAnchorOrigin}
                submenuTransformOrigin={submenuTransformOrigin}
              />
            ))}
          </div>
        </SMenu>
      )}
    </Box>
  );
};

export default memo(SDropdownMenu);
